import { IOption, ISearchFilterSettings } from '@miq/shared-models';
/**
 * Removes filter A if any other filter contains the filter A in association filter array
 * @example
 * [
 *    {key: 1}, // this will be removed/filtered
 *    {
 *      key: 2, // this filter is preserved because it contains filter key 1 in associations
 *      associations: [
 *        {key: 1},
 *        {key: 3}
 *      ]
 *    }
 * ]
 * // output
 * [
 *   {
 *     key: 2, // this filter is preserved because it contains filter key 1 in associations
 *     associations: [
 *       {key: 1},  // <----------- since key 1 was contained here, the single was removed from selection
 *       {key: 3}
 *     ]
 *   }
 * ]
 * @param filters
 */
const keepAssociationOverSingleFilter = (
  filters: IOption[],
  associationAttr: 'values' | 'associations' = 'associations'
): IOption[] => {
  return filters.filter((filter, _, selfFilters) => {
    return selfFilters
      .filter(f => f !== filter)
      .every(otherFilter =>
        !!filter.key &&
        Array.isArray(otherFilter[associationAttr]) &&
        otherFilter[associationAttr].every(option => typeof option === 'object')
          ? // filter excluded if filter is contained in a associations set
            otherFilter[associationAttr].every(option => option.key !== filter.key)
          : true
      );
  });
};
/**
 * Preserve filters (IOption[]) objects with key included within any other self object associationAttr ('values' or 'associations')
 * by calling keepAssociationOverSingleFilter
 * @param filterSections
 * @param associationAttr
 * @returns
 */
export function filterSingleSieve(
  filterSections: Partial<ISearchFilterSettings>,
  associationAttr: 'values' | 'associations' = 'associations'
): Partial<ISearchFilterSettings> {
  return { ...filterSections, filters: keepAssociationOverSingleFilter(filterSections.filters, associationAttr) };
}
/**
 * Facade method to apply filters map, extend or collapse filter selections
 * so far only `keepAssociationOverSingleFilter` it's being used to filter
 * repeated filter items in selection to preserve the filter in associations
 * over the single filter selection
 * @param filterSections
 */
export function filtersSieve(
  filterSections: Partial<ISearchFilterSettings>[],
  associationAttr: 'values' | 'associations' = 'associations'
): Partial<ISearchFilterSettings>[] {
  return filterSections.map(searchFilter => filterSingleSieve(searchFilter, associationAttr));
}
